/**
 * Utility functions for formatting data in the MyTradesHub application
 */

/**
 * Format a number as currency (USD)
 * @param {number} value - The value to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @param {string} currency - The currency code (default: 'USD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, locale = 'en-US', currency = 'USD') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

/**
 * Format a number as a percentage
 * @param {number} value - The value to format (e.g., 0.25 for 25%)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%';
  }
  
  // If value is already in percentage form (e.g., 25 instead of 0.25)
  const normalizedValue = value > 1 && value <= 100 ? value : value * 100;
  
  return `${normalizedValue.toFixed(decimals)}%`;
};

/**
 * Format a date string to a localized date format
 * @param {string|Date} dateString - The date to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, locale = 'en-US', options = {}) => {
  if (!dateString) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  const dateOptions = { ...defaultOptions, ...options };
  
  try {
    const date = new Date(dateString);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided to formatDate:', dateString);
      return 'Invalid Date';
    }
    return new Intl.DateTimeFormat(locale, dateOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a number with thousands separators
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2, locale = 'en-US') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Calculate profit factor (gross profit / gross loss)
 * @param {number} grossProfit - Total gross profit
 * @param {number} grossLoss - Total gross loss (should be a positive number)
 * @returns {string} Formatted profit factor
 */
export const calculateProfitFactor = (grossProfit, grossLoss) => {
  if (!grossLoss || grossLoss === 0) return 'N/A';
  
  const profitFactor = grossProfit / Math.abs(grossLoss);
  return profitFactor.toFixed(2);
};

/**
 * Format a time period for display
 * @param {string} period - The period identifier (day, week, month, quarter, year)
 * @param {string|Date} date - The date associated with the period
 * @returns {string} Formatted period string
 */
export const formatPeriod = (period, date) => {
  const d = new Date(date);
  
  switch (period) {
    case 'day':
      return formatDate(d, 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    case 'week':
      return `Week ${getWeekNumber(d)}, ${d.getFullYear()}`;
    case 'month':
      return formatDate(d, 'en-US', { year: 'numeric', month: 'long' });
    case 'quarter':
      const quarter = Math.floor(d.getMonth() / 3) + 1;
      return `Q${quarter} ${d.getFullYear()}`;
    case 'year':
      return d.getFullYear().toString();
    default:
      return formatDate(d);
  }
};

/**
 * Get the week number for a date
 * @param {Date} date - The date to get the week number for
 * @returns {number} Week number (1-53)
 */
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

/**
 * Generate color based on value (positive/negative)
 * @param {number} value - The value to determine color for
 * @param {string} positiveColor - Color for positive values (default: '#4caf50')
 * @param {string} negativeColor - Color for negative values (default: '#f44336')
 * @param {string} neutralColor - Color for zero values (default: '#9e9e9e')
 * @returns {string} Color code
 */
export const getValueColor = (value, positiveColor = '#4caf50', negativeColor = '#f44336', neutralColor = '#9e9e9e') => {
  if (value === null || value === undefined || isNaN(value) || value === 0) {
    return neutralColor;
  }
  
  return value > 0 ? positiveColor : negativeColor;
};
/**
 * Convert notification HTML (e.g. from buildFillNotificationHTML) to plain text
 * for surfaces that don't render markup — toasts, the alerts panel, browser
 * notifications. Block-level tags become newlines; inline tags are dropped.
 */
export const notificationHtmlToText = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<\s*(br|\/pre|\/p|\/div|\/li|\/tr)\s*\/?\s*>/gi, '\n')
    .replace(/<\s*(pre|p|div|li|tr)\b[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
